import './TypeTester.scss';

import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { PlainTextPlugin } from '@lexical/react/LexicalPlainTextPlugin';
import classNames from 'classnames';
import {
  $createLineBreakNode,
  $createParagraphNode,
  $createTextNode,
  $getRoot,
} from 'lexical';
import { type FC, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import ColorBar from 'components/ColorBar';
import { ErrorBoundary } from 'components/core';
import SelectArrow from 'components/vectors/SelectArrow';
import linearScale from 'utils/linearScale';
import { useFetchFontFaceDeclaration } from './FontFaceDeclarations';
import { LexicalFocusObserver } from './LexicalFocusObserver';

const MIN_RANGE_VALUE = 1;
const MAX_RANGE_VALUE = 100;
const MIN_FONT_SIZE = window.ENV.typeTesters.minFontSize; // em
const MAX_FONT_SIZE = window.ENV.typeTesters.maxFontSize; // em

const scaleRangeValue = linearScale(
  [MIN_RANGE_VALUE, MAX_RANGE_VALUE],
  [MIN_FONT_SIZE, MAX_FONT_SIZE],
);

const scaleFontSize = linearScale(
  [MIN_FONT_SIZE, MAX_FONT_SIZE],
  [MIN_RANGE_VALUE, MAX_RANGE_VALUE],
);

interface Props {
  typeTester: {
    id: number;
    content: string;
    faceId: number;
    fontSize: number;
    lineHeight: number;
    openTypeFeatures: string;
  };
  faces: { id: number; description: string; cssClassName: string }[];
  color: string | null;
}

const TypeTester: FC<Props> = (props) => {
  const { typeTester, faces, color } = props;

  const { t } = useTranslation();

  const [faceId, setFaceId] = useState(typeTester.faceId);
  const [fontSize, setFontSize] = useState(typeTester.fontSize);
  const [focused, setFocused] = useState(false);

  const { unfetchedFontFaceIds, fetchFontFaceDeclaration } =
    useFetchFontFaceDeclaration();

  useEffect(() => {
    fetchFontFaceDeclaration(faceId);
  }, [fetchFontFaceDeclaration, faceId]);

  const face = faces.find(({ id }) => id === faceId);

  const handleFocus = useCallback(() => {
    setFocused(true);
  }, []);

  const handleBlur = useCallback(() => {
    setFocused(false);
  }, []);

  if (face == null) {
    return null;
  }

  return (
    <ColorBar>
      <div
        className={classNames(
          'FamiliesTypeTester',
          'content-wrapper',
          `FamiliesTypeTester-${typeTester.id}`,
          {
            'is-loading': unfetchedFontFaceIds.includes(faceId),
            'is-focused': focused,
          },
        )}
      >
        <div className="FamiliesTypeTester-controls">
          <div className="FamiliesTypeTester-controls-select-wrapper">
            <select
              className="FamiliesTypeTester-controls-select"
              value={faceId}
              onChange={(event) => {
                setFaceId(parseInt(event.currentTarget.value));
              }}
            >
              {faces.map((face) => (
                <option key={face.id} value={face.id}>
                  {face.description}
                </option>
              ))}
            </select>
            <SelectArrow color={color} />
          </div>
          <input
            className="FamiliesTypeTester-controls-input"
            type="range"
            min={MIN_RANGE_VALUE}
            max={MAX_RANGE_VALUE}
            value={scaleFontSize(fontSize)}
            onChange={(event) => {
              setFontSize(scaleRangeValue(parseInt(event.currentTarget.value)));
            }}
          />
        </div>

        <div className="FamiliesTypeTester-content-wrapper">
          <div className="FamiliesTypeTester-content-hint">
            {t('family.typeTesterHint')}
          </div>

          <LexicalComposer
            initialConfig={{
              namespace: 'TypeTester',
              onError: console.error,
              editorState: (editor) => {
                editor.update(() => {
                  const root = $getRoot();

                  const paragraph = $createParagraphNode();
                  const lines = typeTester.content.split(/<br\s*\/?>/);

                  lines.forEach((line, index) => {
                    paragraph.append($createTextNode(line));

                    if (index < lines.length - 1) {
                      paragraph.append($createLineBreakNode());
                    }
                  });

                  root.append(paragraph);
                });
              },
            }}
          >
            <LexicalFocusObserver onFocus={handleFocus} onBlur={handleBlur} />

            <PlainTextPlugin
              contentEditable={
                <ContentEditable
                  className={classNames(
                    'FamiliesTypeTester-content',
                    face.cssClassName,
                  )}
                  style={{
                    fontSize: `${fontSize}em`,
                    lineHeight: `${typeTester.lineHeight}em`,
                    fontFeatureSettings: typeTester.openTypeFeatures,
                    color: color || undefined,
                  }}
                  spellCheck={false}
                />
              }
              placeholder={<></>}
              ErrorBoundary={ErrorBoundary}
            />
          </LexicalComposer>
        </div>
      </div>

      <style>{`
        .FamiliesTypeTester-${typeTester.id} .FamiliesTypeTester-controls-input {
          border-top-color: ${color};
        }

        .FamiliesTypeTester-${typeTester.id} .FamiliesTypeTester-controls-select {
          color: ${color};
        }

        .FamiliesTypeTester-${typeTester.id} input[type='range'].FamiliesTypeTester-controls-input::-webkit-slider-thumb {
          background: ${color};
        }

        .FamiliesTypeTester-${typeTester.id} input[type='range'].FamiliesTypeTester-controls-input::-moz-range-thumb {
          background: ${color};
        }

        .FamiliesTypeTester-${typeTester.id} input[type='range'].FamiliesTypeTester-controls-input::-ms-thumb {
          background: ${color};
        }

        .FamiliesTypeTester-${typeTester.id} .FamiliesTypeTester-content-hint {
          color: ${color};
        }
      `}</style>
    </ColorBar>
  );
};

export default TypeTester;
